import { useCallback, useRef, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { Chip } from '../components/Chips';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Drawer } from '../components/Overlay';
import { VendorIntakeReview } from '../components/VendorIntakeReview';
import { documents } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { DocumentStatus, DocumentStatusLabels, GatewayScenario } from '../lib/enums';
import type { DocumentSummaryDto } from '../lib/types';

type VendorIntakeColumn = 'document' | 'supplier' | 'status' | 'uploaded';

function vendorIntakeSortValue(row: DocumentSummaryDto, key: VendorIntakeColumn): string | number {
  switch (key) {
    case 'document':
      return row.fileName;
    case 'supplier':
      return row.vendorName ?? '';
    case 'status':
      return row.status;
    case 'uploaded':
      return row.createdUtc;
    default:
      return '';
  }
}
/**
 * VENDOR INTAKE — establish and teach a supplier before its invoices are processed.
 *
 * The sequence this screen exists to enforce is the one Business Central already proves: identify
 * the supplier, let a person confirm who they are, and only then let the expensive invoice path
 * interpret their documents. Dropping a bill for an unknown supplier first gets both jobs done
 * badly — the vendor is guessed at, and the extraction has no learned layout to work from.
 *
 * Two rules shape everything here:
 *
 *   Nothing is created until a person approves it. The upload analyses the document; it does not
 *   make a vendor. High confidence changes what is proposed, never whether a human is asked.
 *
 *   What is approved is what is learned. The values in this form are what the QuickBooks vendor is
 *   written from AND what iERP AI is taught, so the next invoice from this supplier is read from a
 *   layout somebody actually confirmed.
 */
export function VendorIntakePage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  // Arriving from a vendor's "Learn from invoice" action, or from a bill whose supplier could not
  // be identified. Either way the intake is bound to that vendor and cannot create a second one.
  const boundVendorId = params.get('vendorId');
  const boundVendorName = params.get('vendorName');

  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(params.get('document'));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const list = useAsync(
    (signal) =>
      documents.list({ scenario: GatewayScenario.NewVendor, page: 1, pageSize: 50 }, signal),
    [],
  );

  const intakeTable = useSortableTable<VendorIntakeColumn>({
    storageKey: 'vendorIntakeColumnWidths',
    columns: {
      document: { minWidth: 160 },
      supplier: { minWidth: 160 },
      status: { minWidth: 130 },
      uploaded: { minWidth: 130 },
    },
  });

  const detail = useAsync(
    (signal) => (selectedId ? documents.get(selectedId, signal) : Promise.resolve(null)),
    [selectedId],
  );

  const upload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setUploading(true);
      try {
        // The SAME uploader, evidence retention and duplicate controls every other document uses —
        // vendor intake is a scenario, not a second ingestion path.
        const handle = documents.upload(files[0], {
          scenario: GatewayScenario.NewVendor,
          qboVendorId: boundVendorId,
          targetQboEntityId: null,
          notes: boundVendorId
            ? `Vendor intake for existing vendor ${boundVendorName ?? boundVendorId}.`
            : 'Vendor intake for a new supplier.',
        });

        const created = await handle.promise;
        toast.success('Analysing the document. The supplier details will appear here shortly.');
        setSelectedId(created.id);
        await list.reload();
      } catch (error) {
        toast.error(errorMessage(error));
      } finally {
        setUploading(false);
      }
    },
    [boundVendorId, boundVendorName, list, toast],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void upload(event.dataTransfer.files);
  };

  return (
    <>
      <PageHeader
        title="Vendor Intake"
        description="Drop an invoice to identify the supplier. Nothing is created in QuickBooks until you approve it."
      />

      {boundVendorId ? (
        <Toolbar>
          <Chip tone="info">
            Teaching {boundVendorName ?? boundVendorId} — this intake updates that vendor and cannot
            create a second one.
          </Chip>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              params.delete('vendorId');
              params.delete('vendorName');
              setParams(params);
            }}
          >
            Clear
          </button>
        </Toolbar>
      ) : null}

      <Panel>
        <div
          className={['dropzone', dragging ? 'dropzone-active' : ''].filter(Boolean).join(' ')}
          onDrop={onDrop}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
        >
          <div className="dropzone-inner">
            <span className="dropzone-icon" aria-hidden="true">
              {uploading ? '…' : '⬇'}
            </span>
            <p className="dropzone-title">
              {uploading ? 'Uploading' : dragging ? 'Release to analyse' : 'Drag a supplier invoice here'}
            </p>
            <p className="dropzone-sub">
              PDF, PNG, JPG or TIFF · only the first page is read, because this identifies the
              supplier rather than processing the invoice
            </p>
            <button
              type="button"
              className="btn btn-primary"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="visually-hidden"
              disabled={uploading}
              accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,image/*"
              aria-label="Choose an invoice to identify the supplier"
              onChange={(event) => {
                void upload(event.target.files);
                event.target.value = '';
              }}
            />
          </div>
        </div>
      </Panel>

      <Panel title="Vendor intakes">
        {list.loading ? (
          <TableSkeleton rows={4} />
        ) : list.error ? (
          <ErrorState message={errorMessage(list.error)} onRetry={list.reload} />
        ) : list.data && list.data.items.length > 0 ? (
          <TableWrap tableClassName="data-table-sortable">
            <colgroup>
              <col style={intakeTable.columnStyle('document')} />
              <col style={intakeTable.columnStyle('supplier')} />
              <col style={intakeTable.columnStyle('status')} />
              <col style={intakeTable.columnStyle('uploaded')} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <SortableTh
                  label="Document"
                  sortKey="document"
                  currentSortKey={intakeTable.sortKey}
                  sortDir={intakeTable.sortDir}
                  onSort={intakeTable.toggleSort}
                  style={intakeTable.columnStyle('document')}
                  onResizeStart={intakeTable.beginResize('document')}
                />
                <SortableTh
                  label="Suggested supplier"
                  sortKey="supplier"
                  currentSortKey={intakeTable.sortKey}
                  sortDir={intakeTable.sortDir}
                  onSort={intakeTable.toggleSort}
                  style={intakeTable.columnStyle('supplier')}
                  onResizeStart={intakeTable.beginResize('supplier')}
                />
                <SortableTh
                  label="Status"
                  sortKey="status"
                  currentSortKey={intakeTable.sortKey}
                  sortDir={intakeTable.sortDir}
                  onSort={intakeTable.toggleSort}
                  style={intakeTable.columnStyle('status')}
                  onResizeStart={intakeTable.beginResize('status')}
                />
                <SortableTh
                  label="Uploaded"
                  sortKey="uploaded"
                  currentSortKey={intakeTable.sortKey}
                  sortDir={intakeTable.sortDir}
                  onSort={intakeTable.toggleSort}
                  style={intakeTable.columnStyle('uploaded')}
                  onResizeStart={intakeTable.beginResize('uploaded')}
                />
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {intakeTable.sort(list.data.items, vendorIntakeSortValue).map((row: DocumentSummaryDto) => (
                <tr key={row.id}>
                  <td>{row.fileName}</td>
                  <td>{row.vendorName ?? <span className="muted">Not identified yet</span>}</td>
                  <td>
                    <Chip tone={row.status === DocumentStatus.Posted ? 'good' : 'neutral'}>
                      {row.status === DocumentStatus.Posted
                        ? 'Vendor created'
                        : DocumentStatusLabels[row.status]}
                    </Chip>
                  </td>
                  <td>{formatDateTime(row.createdUtc)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setSelectedId(row.id)}
                    >
                      {row.status === DocumentStatus.Posted ? 'View' : 'Review'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        ) : (
          <EmptyState
            title="No vendor intakes yet"
            description="Drop a supplier's invoice above to identify them before processing their bills."
          />
        )}
      </Panel>

      <Drawer
        open={selectedId !== null}
        title="Review supplier"
        onClose={() => {
          setSelectedId(null);
          void list.reload();
        }}
      >
        {detail.loading ? (
          <TableSkeleton rows={6} />
        ) : detail.error ? (
          <ErrorState message={errorMessage(detail.error)} onRetry={detail.reload} />
        ) : detail.data ? (
          <VendorIntakeReview
            detail={detail.data}
            documentId={selectedId!}
            boundVendorId={boundVendorId}
            onDone={async () => {
              setSelectedId(null);
              await list.reload();
            }}
          />
        ) : null}
      </Drawer>
    </>
  );
}
